import { RefObject, useLayoutEffect, useRef, useState } from 'react';

/**
 * useOverflowDetection - кастомный React-хук для определения, переполняется ли элемент по горизонтали или вертикали (т.е. если его содержимое выходит за границы).
 *
 * @returns {[RefObject<T>, boolean]} - Массив с двумя элементами:
 * - `ref`: объект ссылки (RefObject) на элемент, для которого нужно отслеживать переполнение.
 * - `isOverflowing`: булево значение, указывающее, происходит ли переполнение.
 *
 * @template T - Тип элемента DOM, который будет отслеживаться (например, `HTMLDivElement`).
 */

export const useOverflowDetection = <T extends HTMLElement>(): [
  RefObject<T>,
  boolean,
] => {
  const ref = useRef<T>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    const detectOverflow = () => {
      if (ref.current) {
        const { clientWidth, clientHeight, scrollWidth, scrollHeight } =
          ref.current;

        setIsOverflowing(
          clientWidth < scrollWidth || clientHeight < scrollHeight
        );
      }
    };

    const resizeObserver = new ResizeObserver(detectOverflow);
    resizeObserver.observe(ref.current);

    detectOverflow();

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref]);

  return [ref, isOverflowing];
};
